// Intake field locks. Merchant fields the operator pre-filled on
// NewApplication are locked on the intake form: shown read-only there, and
// stripped from every merchant save by the intake API
// (sharedModules.strip_intake_locked_fields), so pre-populated information --
// business / owner name, ZIP, bank details, ... -- can't be overwritten.
//
// Paths are merchantData dotted paths, which are also the intake form's field
// ids (see intakeFieldPath for the few that differ). A field locks only when
// the operator actually entered something: a value equal to the empty form's
// default (e.g. PUBCORP / PRESIDENT, 100% swiped, US) was never chosen and
// stays editable.

import {
  EMPTY_ADDRESS,
  EMPTY_BANK,
  EMPTY_BUSINESS,
  EMPTY_MODE,
  EMPTY_OWNER,
  EMPTY_VOLUME,
  type AddressForm,
  type BankForm,
  type BusinessForm,
  type ModeForm,
  type OwnerForm,
  type VolumeDetailsForms,
} from "@/components/application";

export interface OperatorEnteredInput {
  biz: BusinessForm;
  bizAddr: AddressForm;
  mailAddr: AddressForm;
  mailSame: boolean;
  owner: OwnerForm;
  ownerAddr: AddressForm;
  ownerAddrSame: boolean;
  vol: VolumeDetailsForms;
  deposit: BankForm;
  withdrawal: BankForm;
  withdrawalSame: boolean;
  mode: ModeForm;
}

const ADDRESS_LEAVES = [
  "address1",
  "address2",
  "city",
  "stateCd",
  "zip",
  "countryCd",
] as const;
const BANK_LEAVES = ["accountNumber", "routingNumber", "accountType", "bankName"] as const;
const BUSINESS_FIELDS = [
  "businessName",
  "contactEmail",
  "contactPhone",
  "dbaName",
  "taxFilingName",
  "taxFilingMethod",
  "taxID",
  "mcc",
  "storeType",
  "businessDescription",
  "businessStartDate",
  "website",
] as const;
const OWNER_FIELDS = [
  "firstName",
  "lastName",
  "title",
  "email",
  "phone",
  "ownershipTypeCd",
  "ssn",
  "dateOfBirth",
  "ownershipPct",
  "driversLicenseNumber",
  "driversLicenseStateCd",
] as const;
const VOLUME_FIELDS = [
  "averageMonthlyVolume",
  "highTicketAmount",
  "averageTicketAmount",
] as const;
const MODE_FIELDS = ["swipedPct", "keyedPct", "eCommercePct", "mailOrderPct"] as const;

/** Entered = non-blank and different from the empty form's default. */
function entered(value: string | undefined, emptyValue: string | undefined): boolean {
  const v = (value ?? "").trim();
  return v !== "" && v !== (emptyValue ?? "").trim();
}

function enteredLeaves<T extends Record<string, string>>(
  value: T,
  empty: T,
  leaves: readonly (keyof T & string)[],
): string[] {
  return leaves.filter((leaf) => entered(value[leaf], empty[leaf]));
}

/** The merchantData paths the operator filled in, to lock on the intake. */
export function operatorEnteredPaths(input: OperatorEnteredInput): string[] {
  const paths: string[] = [];
  const add = (prefix: string, leaves: string[]) =>
    leaves.forEach((leaf) => paths.push(prefix ? `${prefix}.${leaf}` : leaf));

  const bizLeaves = enteredLeaves(input.biz, EMPTY_BUSINESS, BUSINESS_FIELDS);
  // A pre-filled tax ID is only meaningful with its filing method, so the
  // method locks with it (even when left at the default EIN).
  if (bizLeaves.includes("taxID") && !bizLeaves.includes("taxFilingMethod"))
    bizLeaves.push("taxFilingMethod");
  add("", bizLeaves);

  const bizAddrLeaves = enteredLeaves(input.bizAddr, EMPTY_ADDRESS, ADDRESS_LEAVES);
  add("businessAddress", bizAddrLeaves);
  // "Same as business" means the business address is what goes on the wire.
  add(
    "mailingAddress",
    input.mailSame
      ? bizAddrLeaves
      : enteredLeaves(input.mailAddr, EMPTY_ADDRESS, ADDRESS_LEAVES),
  );

  add("owner", enteredLeaves(input.owner, EMPTY_OWNER, OWNER_FIELDS));
  add(
    "owner.address",
    input.ownerAddrSame
      ? bizAddrLeaves
      : enteredLeaves(input.ownerAddr, EMPTY_ADDRESS, ADDRESS_LEAVES),
  );

  add("volumeDetails", enteredLeaves(input.vol, EMPTY_VOLUME, VOLUME_FIELDS));

  const depositLeaves = enteredLeaves(input.deposit, EMPTY_BANK, BANK_LEAVES);
  add("bankDetail.deposit", depositLeaves);
  add(
    "bankDetail.withdrawal",
    input.withdrawalSame
      ? depositLeaves
      : enteredLeaves(input.withdrawal, EMPTY_BANK, BANK_LEAVES),
  );

  // The transaction mix must total 100: if the operator set any part of it,
  // lock all four so the merchant can't be left with a total they can't fix.
  if (enteredLeaves(input.mode, EMPTY_MODE, MODE_FIELDS).length > 0)
    add("modeOfTransaction", [...MODE_FIELDS]);

  return paths;
}

// Intake field ids that aren't their merchantData path.
const VOLUME_IDS = new Set<string>(VOLUME_FIELDS);

/** The merchantData path an intake field id writes to. */
export function intakeFieldPath(id: string): string {
  if (VOLUME_IDS.has(id)) return `volumeDetails.${id}`;
  // The confirmation input locks with the account number it confirms.
  if (id.endsWith(".confirmAccountNumber"))
    return id.replace(/\.confirmAccountNumber$/, ".accountNumber");
  return id;
}
